// src/components/flight-data/ConcourseMap.js
import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Component to display a visual map of the C-Concourse with active gates highlighted
 */
// --- MODIFIED: Added 'hotGates' to the props this component receives ---
function ConcourseMap({ tdrData, activeGates = [], inboundGate, hotGates = [] }) {
  // Convert inbound gate to a number
  const inboundGateNum = parseInt(inboundGate?.replace(/\D/g, ''), 10);

  // Convert active gates to numbers for easier comparison
  const numericActiveGates = activeGates
    .map(gate =>
      typeof gate === 'string' ? parseInt(gate.replace(/\D/g, ''), 10) : gate
    )
    .filter(gate => !isNaN(gate));

  // Calculate proportions - west is about 1/3, east is about 2/3
  const westProportion = 33; // percentage
  const eastProportion = 67; // percentage

  // Define tunnel locations
  const tunnels = [
    { position: 31.3, label: 'West Tunnel', section: 'west' },
    { position: 47.2, label: 'East Tunnel', section: 'east' },
    { position: 55.5, label: '', section: 'east' },
    { position: 63.5, label: '', section: 'east' }
  ];

  // --- MODIFIED: This function now checks for hot gates ---
  const getGateColor = (gateNum) => {
    // Priority 1: Inbound gate is always gold
    if (gateNum === inboundGateNum) {
      return { bg: '#F9B612', text: '#000' };
    }
    // Priority 2: Hot connection gates are red
    if (hotGates.includes(gateNum)) {
      return { bg: '#FF0000', text: '#fff' };
    }
    // Priority 3: Other active outbound gates are green
    if (numericActiveGates.includes(gateNum)) {
      return { bg: '#4CAF50', text: '#fff' };
    }
    // Default for inactive gates
    return { bg: '#333', text: '#aaa' };
  };

  // Helper to get gate style (no changes needed here)
  const getGateStyle = (gateNum) => {
    const colors = getGateColor(gateNum);
    return {
      width: 28,
      height: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bg,
      color: colors.text,
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: gateNum === inboundGateNum ? 'bold' : 'normal',
      border: 'none'
    };
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: '#1f2937', mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#e2e2e2' }}>
          C-Concourse Map
        </Typography>

        <Tooltip
          title="Gate layout for Southwest Airlines at C-Concourse. Odd gates on North side, Even gates on South side."
          arrow
        >
          <IconButton size="small" sx={{ color: '#e2e2e2' }}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ position: 'relative', width: '100%', height: '200px', overflow: 'hidden' }}>
        {/* North Side (Odd gates) */}
        <Box
          sx={{
            position: 'absolute',
            top: '40px',
            left: '0',
            width: '100%',
            display: 'flex',
          }}
        >
          {/* West wing - about 1/3 of the width */}
          <Box
            sx={{
              display: 'flex',
              width: `${westProportion}%`,
              paddingRight: '10px',
              position: 'relative'
            }}
          >
            {/* Gates positioned with variable spacing */}
            <Box sx={{ position: 'absolute', left: '0%' }}>
              <Box sx={getGateStyle(23)}>23</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '10%' }}>
              <Box sx={getGateStyle(25)}>25</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '20%' }}>
              <Box sx={getGateStyle(27)}>27</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '30%' }}>
              <Box sx={getGateStyle(29)}>29</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '40%' }}>
              <Box sx={getGateStyle(31)}>31</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '57%' }}>
              <Box sx={getGateStyle(33)}>33</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '67.5%' }}>
              <Box sx={getGateStyle(35)}>35</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '78%' }}>
              <Box sx={getGateStyle(37)}>37</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '88.5%' }}>
              <Box sx={getGateStyle(39)}>39</Box>
            </Box>
          </Box>

          {/* East wing - about 2/3 of the width */}
          <Box
            sx={{
              display: 'flex',
              width: `${eastProportion}%`,
              paddingLeft: '10px',
              position: 'relative'
            }}
          >
            {/* Gates positioned with variable spacing */}
            <Box sx={{ position: 'absolute', left: '2%' }}>
              <Box sx={getGateStyle(41)}>41</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '8%' }}>
              <Box sx={getGateStyle(43)}>43</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '14%' }}>
              <Box sx={getGateStyle(45)}>45</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '20%' }}>
              <Box sx={getGateStyle(47)}>47</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '29.7%' }}>
              <Box sx={getGateStyle(49)}>49</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '37.1%' }}>
              <Box sx={getGateStyle(51)}>51</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '44%' }}>
              <Box sx={getGateStyle(53)}>53</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '50.6%' }}>
              <Box sx={getGateStyle(55)}>55</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '60.3%' }}>
              <Box sx={getGateStyle(57)}>57</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '67.4%' }}>
              <Box sx={getGateStyle(59)}>59</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '74%' }}>
              <Box sx={getGateStyle(61)}>61</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '80.6%' }}>
              <Box sx={getGateStyle(63)}>63</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '90%' }}>
              <Box sx={getGateStyle(65)}>65</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '95%' }}>
              <Box sx={getGateStyle(67)}>67</Box>
            </Box>
          </Box>
        </Box>

        {/* Concourse Lines */}
        <Box
          sx={{
            position: 'absolute',
            top: '70px',
            left: '0',
            width: `${westProportion - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '70px',
            right: '0',
            width: `${eastProportion - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '130px',
            left: '0',
            width: `${westProportion - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '130px',
            right: '0',
            width: `${eastProportion - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />

        {/* South Side (Even gates) */}
        <Box
          sx={{
            position: 'absolute',
            top: '135px',
            left: '0',
            width: '100%',
            display: 'flex',
          }}
        >
          {/* West wing - about 1/3 of the width */}
          <Box
            sx={{
              display: 'flex',
              width: `${westProportion}%`,
              paddingRight: '10px',
              position: 'relative'
            }}
          >
            {/* Gates positioned with variable spacing */}
            <Box sx={{ position: 'absolute', left: '0%' }}>
              <Box sx={getGateStyle(24)}>24</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '14%' }}>
              <Box sx={getGateStyle(26)}>26</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '27%' }}>
              <Box sx={getGateStyle(28)}>28</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '40%' }}>
              <Box sx={getGateStyle(30)}>30</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '57%' }}>
              <Box sx={getGateStyle(32)}>32</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '67.5%' }}>
              <Box sx={getGateStyle(34)}>34</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '78%' }}>
              <Box sx={getGateStyle(36)}>36</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '88.5%' }}>
              <Box sx={getGateStyle(38)}>38</Box>
            </Box>
          </Box>

          {/* East wing - about 2/3 of the width */}
          <Box
            sx={{
              display: 'flex',
              width: `${eastProportion}%`,
              paddingLeft: '10px',
              position: 'relative'
            }}
          >
            {/* Gates positioned with variable spacing */}
            <Box sx={{ position: 'absolute', left: '2%' }}>
              <Box sx={getGateStyle(40)}>40</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '8%' }}>
              <Box sx={getGateStyle(42)}>42</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '14%' }}>
              <Box sx={getGateStyle(44)}>44</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '20%' }}>
              <Box sx={getGateStyle(46)}>46</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '29.7%' }}>
              <Box sx={getGateStyle(48)}>48</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '37.1%' }}>
              <Box sx={getGateStyle(50)}>50</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '44%' }}>
              <Box sx={getGateStyle(52)}>52</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '50.6%' }}>
              <Box sx={getGateStyle(54)}>54</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '60.3%' }}>
              <Box sx={getGateStyle(56)}>56</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '67.4%' }}>
              <Box sx={getGateStyle(58)}>58</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '74%' }}>
              <Box sx={getGateStyle(60)}>60</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '80.6%' }}>
              <Box sx={getGateStyle(62)}>62</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '90%' }}>
              <Box sx={getGateStyle(64)}>64</Box>
            </Box>
            <Box sx={{ position: 'absolute', left: '95%' }}>
              <Box sx={getGateStyle(66)}>66</Box>
            </Box>
          </Box>
        </Box>

        {/* Tunnels / Breezeways */}
        {tunnels.map((tunnel, index) => {
          let leftPosition;
          if (tunnel.section === 'west') {
            const relativePos = (tunnel.position - 23) / (39 - 23);
            leftPosition = `calc(${relativePos * westProportion}%)`;
          } else {
            const relativePos = (tunnel.position - 40) / (67 - 40);
            leftPosition = `calc(${westProportion}% + ${relativePos * eastProportion}%)`;
          }
          return (
            <Box
              key={`tunnel-${index}`}
              sx={{
                position: 'absolute',
                left: leftPosition,
                top: '70px',
                transform: 'translateX(-7px)',
              }}
            >
              <Box
                sx={{
                  width: tunnel.label === 'Breezeway' ? 16 : 20,
                  height: 60,
                  backgroundColor: '#e2e2e2',
                  borderRadius: 0,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '-45px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: tunnel.label === 'Breezeway' ? '0.5rem' : '0.8rem',
                  fontWeight: 'bold',
                  color: '#f9b612',
                  textAlign: 'center',
                }}
              >
                {tunnel.label === 'West Tunnel' ? (
                  <>West<br />Tunnel</>
                ) : tunnel.label === 'East Tunnel' ? (
                  <>East<br />Tunnel</>
                ) : (
                  tunnel.label
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* --- MODIFIED: The Legend to include 'Hot Connection' --- */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#F9B612', mr: 1, borderRadius: '2px' }}/>
          <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Inbound Gate
          </Typography>
        </Box>
        {/* --- ADDED: New legend item for hot gates --- */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#FF0000', mr: 1, borderRadius: '2px' }}/>
          <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Hot Connection (≤30 min)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#4CAF50', mr: 1, borderRadius: '2px' }}/>
          <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Outbound Gate
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#e2e2e2', mr: 1, borderRadius: '2px' }}/>
          <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Tunnel/Breezeway
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default ConcourseMap;